import { shiftMinutes, workedShiftFraction } from './flags';

export interface AttendanceRecord {
  workerId: string;
  positionId: string;
  date: string;
  /** Horario PROGRAMADO de la posición ('HH:mm:ss'). Es el denominador de la regla del
   *  medio día: sin él no se puede saber si la persona se fue antes de tiempo. */
  startTime: string;
  endTime: string;
  rateAmount: number;
  rateUnit: 'jornada' | 'hora';
  kind: 'ingreso' | 'salida';
  deviceTs: string;
  status: 'pendiente' | 'validada' | 'observada';
}

/** Umbral por defecto: el mismo valor que la 0016 dejó en `app_settings`. */
export const DEFAULT_HALF_DAY_THRESHOLD_PCT = 75;

export interface PayrollLineCalc {
  workerId: string;
  daysWorked: number;
  hoursWorked: number;
  /** Lo que se paga: jornadas (ya con el recargo de feriado) **más** el bono. */
  amount: number;
  /**
   * El bono de asistencia, aparte. Va sumado en `amount` porque eso es lo que se transfiere, pero
   * se expone solo para que la planilla pueda decir «S/X de jornadas + S/100 de bono» en vez de un
   * total que nadie puede reconstruir.
   */
  bono: number;
  /**
   * Lo que agregó el recargo de feriado, aparte: un día de S/60 a ×2 paga 120 y acá van los 60 de
   * más. Igual que el bono, ya está sumado en `amount`; va suelto porque la planilla lo pide en su
   * propia columna (usuario, 3-nov). Un feriado a medio día aporta la mitad.
   */
  feriado: number;
  /**
   * Tarifa base «de la persona»: la que gobierna MÁS días del periodo.
   *
   * Es la columna «Monto diario» de la planilla, que tiene UNA celda por persona. Con una sola
   * tarifa es esa; con varias, gana la mayoritaria —regla del usuario del 2026-09-02: *«si tuvo
   * más días de 60 sale 60»*—. `null` solo cuando **empatan**, y entonces la planilla muestra las
   * dos («60 / 70»): elegir una a cara o cruz sería inventar.
   *
   * También `null` con tarifa por hora, donde «monto diario» no significa nada. El recargo de
   * feriado nunca entra acá: multiplica la tarifa del día, no la del contrato.
   */
  rateAmount: number | null;
  /**
   * Cada tarifa base y cuántos días pagó, de mayor a menor. Vacío con tarifa por hora.
   *
   * Es lo que deja reconstruir la celda cuando hay empate y lo que sostiene la nota que explica la
   * mezcla. Sin esto, un «60 / 70» en la planilla no se podría justificar ante nadie.
   */
  rateBreakdown: [number, number][];
  notes: string[];
}

/**
 * Lo que el cálculo necesita saber del mundo y no puede deducir de las marcas.
 *
 * Va como objeto y no como más parámetros posicionales: `computePayroll` ya recibía cuatro y
 * agregarle tres sueltos convierte cualquier llamada en un acertijo.
 */
export interface PayrollOptions {
  /**
   * `'YYYY-MM-DD'` → multiplicador de la tarifa de ese día. Sale de la tabla `holidays` (0077).
   * Ausente = 1. Se aplica a la TARIFA, no a las horas: la tarifa es por jornada.
   */
  holidays?: Map<string, number>;
  /**
   * Monto del bono por asistir a todos los días asignados (`app_settings.bono_asistencia_monto`).
   * En 0 el bono no se calcula, que es lo correcto para una campaña que no lo tiene.
   */
  bonoAsistencia?: number;
  /**
   * Trabajador → días que tenía ASIGNADOS en el periodo. Sin esto el bono no se puede decidir: no
   * es lo mismo no faltar teniendo 27 días que no faltar teniendo 1.
   */
  assignedDays?: Map<string, number>;
}

export interface AuthorizationRecord {
  workerId: string;
  kind: 'tardanza' | 'falta' | 'permiso';
  eventDate: string;
  status: 'pendiente' | 'aprobado' | 'rechazado';
}

export interface AttendancePair {
  workerId: string;
  positionId: string;
  date: string;
  startTime: string;
  endTime: string;
  rateAmount: number;
  rateUnit: 'jornada' | 'hora';
  entry: AttendanceRecord | null;
  exit: AttendanceRecord | null;
}

const MS_PER_HOUR = 3_600_000;

/**
 * Redondeo monetario a 2 decimales, medio centavo hacia arriba.
 * `toPrecision(12)` limpia el ruido binario (7.005 * 100 = 700.4999999999999)
 * antes de `Math.round`, que si no redondearía 7.005 a 7.00.
 */
export function roundMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  const cents = Math.round(Number((Math.abs(n) * 100).toPrecision(12)));
  return (n < 0 ? -cents : cents) / 100;
}

const ms = (iso: string): number => new Date(iso).getTime();

/**
 * Agrupa los marcados en pares ingreso/salida por (trabajador, posición).
 * Ante duplicados conserva el ingreso más temprano y la salida más tardía.
 * Salida ordenada por trabajador, fecha y posición.
 */
export function pairAttendance(records: AttendanceRecord[]): AttendancePair[] {
  const pairs = new Map<string, AttendancePair>();
  for (const r of records) {
    const key = `${r.workerId}|${r.positionId}`;
    let pair = pairs.get(key);
    if (!pair) {
      pair = {
        workerId: r.workerId, positionId: r.positionId, date: r.date,
        startTime: r.startTime, endTime: r.endTime,
        rateAmount: r.rateAmount, rateUnit: r.rateUnit, entry: null, exit: null,
      };
      pairs.set(key, pair);
    }
    if (r.kind === 'ingreso') {
      if (!pair.entry || ms(r.deviceTs) < ms(pair.entry.deviceTs)) pair.entry = r;
    } else if (!pair.exit || ms(r.deviceTs) > ms(pair.exit.deviceTs)) {
      pair.exit = r;
    }
  }
  return [...pairs.values()].sort(
    (a, b) =>
      a.workerId.localeCompare(b.workerId) ||
      a.date.localeCompare(b.date) ||
      a.positionId.localeCompare(b.positionId),
  );
}

interface Accumulator {
  daysWorked: number;
  hoursWorked: number;
  amount: number;
  missingExit: string[];
  invalidRange: string[];
  halfDays: string[];
  unvalidated: number;
  /** Fechas en las que la persona se presentó. Es la medida del bono, no `daysWorked`. */
  diasConIngreso: Set<string>;
  /** Notas de los días con recargo por feriado, para que el monto se pueda reconstruir. */
  feriados: string[];
  /** Soles que agregó el recargo de feriado, ya incluidos en `amount`. */
  recargoFeriado: number;
  /** Tarifa base por jornada → días que pagó. Decide cuál es la tarifa «de la persona». */
  tarifas: Map<number, number>;
  /** Hubo algún turno pagado por hora: entonces «monto diario» no aplica y queda vacío. */
  porHora: boolean;
}

/** Número "para leer": 5.60 → "5.6", 7.33 → "7.33", 9 → "9". */
function num(n: number): string {
  return String(Number(n.toFixed(2)));
}

/**
 * Porcentaje para la nota. Si al redondear alcanzara el umbral se muestra con un
 * decimal: una nota que dijera "75% del turno, menos del 75% exigido" no explicaría
 * nada, y esta nota existe justamente para que el trabajador pueda reclamar con dato.
 */
function pctText(pct: number, thresholdPct: number): string {
  const rounded = Math.round(pct);
  return rounded < thresholdPct ? String(rounded) : String(Math.floor(pct * 10) / 10);
}

/**
 * Las tarifas del periodo para leer en una celda: «60» si hubo una sola, «60 (20 d) · 70 (7 d)» si hubo
 * varias, vacío si no hubo ninguna por jornada. Recibe `rateBreakdown`, ya ordenado de más días a menos.
 */
export function textoTarifas(desgloseTarifas: [number, number][]): string {
  if (desgloseTarifas.length === 0) return '';
  if (desgloseTarifas.length === 1) return num(desgloseTarifas[0][0]);
  return desgloseTarifas.map(([t, d]) => `${num(t)} (${num(d)} d)`).join(' · ');
}

/** Tarifas base y sus días, de más días a menos; a igual día, la tarifa mayor primero. */
function desglose(a: Accumulator): [number, number][] {
  if (a.porHora) return [];
  return [...a.tarifas.entries()].sort((x, y) => y[1] - x[1] || y[0] - x[0]);
}

/**
 * La tarifa que gobierna más días. `null` si empatan las dos primeras (o si se pagó por hora):
 * con un empate no hay mayoritaria, y la planilla muestra las dos en vez de elegir al azar.
 */
function tarifaDominante(a: Accumulator): number | null {
  const d = desglose(a);
  if (d.length === 0) return null;
  if (d.length > 1 && d[0][1] === d[1][1]) return null;
  return d[0][0];
}

/**
 * Calcula la planilla por trabajador. Solo cuentan las asistencias `validada`.
 *
 * **Días con decimal (regla del medio día, `half_day_threshold_pct`, hoy 75):** un
 * ingreso validado vale 1 día; si además hay salida validada y el tiempo trabajado
 * (salida − ingreso) es MENOR al umbral de la duración programada del turno, ese día
 * vale 0.5 y la línea lleva una nota que dice horas trabajadas, horas del turno,
 * porcentaje y umbral. Las planillas reales de la empresa pagan 17.5, 24.5 y 0.6 días:
 * contar solo jornadas enteras no cuadraba con la forma real de pagar.
 *
 * Con tarifa por **jornada** el medio día paga media tarifa. Con tarifa por **hora** el
 * monto no se toca (se pagan las horas realmente trabajadas); el 0.5 queda igual en
 * `daysWorked` porque describe la asistencia, no el pago.
 *
 * Sin salida validada NO se aplica la regla: 1 día, 0 h y nota "Sin salida el …" (el
 * coordinador la fija a mano con `set_manual_exit`). Una salida anterior al ingreso
 * tampoco es medio día: es un rango roto y tiene su propia nota.
 *
 * Las autorizaciones `aprobado` no suman pago: `falta`/`permiso` agregan la nota
 * "Falta justificada el YYYY-MM-DD" y `tardanza` la nota "Tardanza justificada…".
 * `periodWorkerIds` son los trabajadores con asignación aprobada en el periodo: se
 * siembran para que quien no marcó nada (p. ej. tuvo permiso toda la semana) igual
 * aparezca en el cierre con su justificación anotada.
 */
export function computePayroll(
  records: AttendanceRecord[],
  authorizations: AuthorizationRecord[] = [],
  periodWorkerIds: string[] = [],
  halfDayThresholdPct: number = DEFAULT_HALF_DAY_THRESHOLD_PCT,
  options: PayrollOptions = {},
): PayrollLineCalc[] {
  const feriados = options.holidays ?? new Map<string, number>();
  const montoBono = options.bonoAsistencia ?? 0;
  const diasAsignados = options.assignedDays ?? new Map<string, number>();
  const acc = new Map<string, Accumulator>();
  const get = (workerId: string): Accumulator => {
    let a = acc.get(workerId);
    if (!a) {
      a = {
        daysWorked: 0, hoursWorked: 0, amount: 0,
        missingExit: [], invalidRange: [], halfDays: [], unvalidated: 0,
        // Fechas en las que la persona SE PRESENTÓ. Es la medida del bono, y no `daysWorked`:
        // un turno corto o un día sin salida marcada valen como asistencia —fue— aunque paguen
        // medio o nada. Decisión del usuario del 1-sep.
        diasConIngreso: new Set<string>(),
        feriados: [],
        recargoFeriado: 0,
        tarifas: new Map<number, number>(),
        porHora: false,
      };
      acc.set(workerId, a);
    }
    return a;
  };

  // Semilla: todo trabajador asignado en el periodo tiene línea, marque o no.
  for (const workerId of periodWorkerIds) get(workerId);

  /**
   * Días con autorización APROBADA, por trabajador. Se arma antes del recorrido porque el medio
   * día se decide ahí dentro.
   *
   * Entran las tres clases (`permiso`, `falta`, `tardanza`): lo que repone el día no es el motivo
   * sino el hecho de que alguien con autoridad ya lo aprobó. Una tardanza justificada que dejó el
   * turno por debajo del umbral está tan autorizada como un permiso de salida.
   */
  const autorizado = new Set<string>();
  for (const z of authorizations) {
    if (z.status === 'aprobado') autorizado.add(`${z.workerId}|${z.eventDate}`);
  }

  // Todo trabajador con algún marcado tiene línea; las no validadas se cuentan aparte.
  for (const r of records) {
    const a = get(r.workerId);
    if (r.status !== 'validada') a.unvalidated += 1;
  }

  for (const pair of pairAttendance(records)) {
    const entry = pair.entry && pair.entry.status === 'validada' ? pair.entry : null;
    if (!entry) continue;
    const exit = pair.exit && pair.exit.status === 'validada' ? pair.exit : null;
    const a = get(pair.workerId);
    // Se presentó: cuenta para el bono aunque el día pague medio o nada.
    a.diasConIngreso.add(pair.date);

    let hours = 0;
    // `fraction` = qué parte del turno programado trabajó. null = no se puede saber
    // (sin salida, o salida anterior al ingreso): en ese caso el día cuenta entero.
    let fraction: number | null = null;
    if (!exit) {
      a.missingExit.push(pair.date);
    } else {
      const delta = ms(exit.deviceTs) - ms(entry.deviceTs);
      if (delta > 0) hours = roundMoney(delta / MS_PER_HOUR);
      else a.invalidRange.push(pair.date);
      fraction = workedShiftFraction({
        positionDate: pair.date,
        startTime: pair.startTime,
        endTime: pair.endTime,
        entryDeviceTs: entry.deviceTs,
        exitDeviceTs: exit.deviceTs,
        halfDayThresholdPct,
      });
    }

    const cortoDeMas = fraction !== null && fraction * 100 < halfDayThresholdPct;
    /**
     * El 0.5 castiga trabajar menos del turno **sin avisar**. Con una autorización aprobada
     * alguien con autoridad ya decidió que la ausencia es legítima, así que el día se repone
     * entero (decisión del usuario, 2026-08-15: *"claramente, si está autorizado, sería el día
     * completo"*). Descontar igual convertiría el permiso en un permiso a medias, y nadie lo
     * pediría por el portal — que es justo el circuito que se quiere que usen.
     */
    const repuesto = cortoDeMas && autorizado.has(`${pair.workerId}|${pair.date}`);
    const isHalfDay = cortoDeMas && !repuesto;
    const diaPagado = isHalfDay ? 0.5 : 1;
    a.daysWorked += diaPagado;
    // La tarifa se cuenta en DÍAS, no en turnos: un medio día pesa la mitad al decidir cuál es la
    // tarifa mayoritaria, igual que pesa la mitad en el pago.
    if (pair.rateUnit === 'jornada') {
      a.tarifas.set(pair.rateAmount, (a.tarifas.get(pair.rateAmount) ?? 0) + diaPagado);
    } else {
      a.porHora = true;
    }
    if (cortoDeMas && fraction !== null) {
      const shiftHours = shiftMinutes(pair.date, pair.startTime, pair.endTime) / 60;
      const trabajado =
        `trabajó ${num(hours)} h de las ${num(shiftHours)} h del turno ` +
        `(${pctText(fraction * 100, halfDayThresholdPct)}%)`;
      // La nota se conserva SIEMPRE, incluso al reponer: si desaparece, el cierre muestra un día
      // entero sin rastro de que fue corto y nadie puede auditar por qué se pagó completo.
      a.halfDays.push(
        repuesto
          ? `Día completo el ${pair.date} pese a turno corto: ${trabajado}, `
            + 'repuesto por autorización aprobada'
          : `Medio día el ${pair.date}: ${trabajado}, menos del `
            + `${num(halfDayThresholdPct)}% exigido para contar el día completo`,
      );
    }

    a.hoursWorked = roundMoney(a.hoursWorked + hours);
    // Por jornada el medio día paga media tarifa. Por hora el monto NO se toca: ahí ya
    // se pagan las horas efectivamente trabajadas y recortarlo sería cobrarlo dos veces.
    /**
     * Feriado: multiplica la TARIFA del día, no las horas. Un feriado a 2 sobre una jornada de
     * S/60 paga S/120; si además el turno fue corto, paga la mitad de eso (S/60). El orden importa
     * y es este: primero el recargo, después el medio día — al revés, un feriado trabajado a medias
     * pagaría más que un día normal completo.
     */
    const multiplicador = feriados.get(pair.date) ?? 1;
    const tarifaDia = pair.rateAmount * multiplicador;
    const fullRate = pair.rateUnit === 'jornada' ? tarifaDia : hours * tarifaDia;
    const pay = roundMoney(pair.rateUnit === 'jornada' && isHalfDay ? fullRate / 2 : fullRate);
    a.amount = roundMoney(a.amount + pay);
    if (multiplicador !== 1) {
      // Lo que el mismo día habría pagado sin feriado, con la misma regla del medio día.
      const sinFeriado = pair.rateUnit === 'jornada' ? pair.rateAmount : hours * pair.rateAmount;
      const paySinFeriado = roundMoney(pair.rateUnit === 'jornada' && isHalfDay ? sinFeriado / 2 : sinFeriado);
      a.recargoFeriado = roundMoney(a.recargoFeriado + pay - paySinFeriado);
      a.feriados.push(
        `Feriado el ${pair.date}: la jornada paga ×${num(multiplicador)} `
        + `(S/${num(pair.rateAmount)} → S/${num(tarifaDia)})`,
      );
    }
  }

  // Autorizaciones aprobadas: solo nota, sin pago, y solo de trabajadores con línea.
  // `falta`/`permiso` → "Falta justificada"; `tardanza` → "Tardanza justificada".
  const justified = new Map<string, Set<string>>();
  const lateJustified = new Map<string, Set<string>>();
  for (const z of authorizations) {
    if (z.status !== 'aprobado') continue;
    if (!acc.has(z.workerId)) continue;
    const target = z.kind === 'tardanza' ? lateJustified : justified;
    const set = target.get(z.workerId) ?? new Set<string>();
    set.add(z.eventDate);
    target.set(z.workerId, set);
  }

  return [...acc.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([workerId, a]) => {
      const notes: string[] = [];
      // El medio día va primero: es la nota que explica por qué el monto no es el
      // esperado, y es la que el trabajador va a querer discutir con dato en la mano.
      for (const n of [...a.halfDays].sort()) notes.push(n);
      for (const d of [...a.missingExit].sort()) notes.push(`Sin salida el ${d}`);
      for (const d of [...a.invalidRange].sort()) notes.push(`Salida anterior al ingreso el ${d}`);
      for (const d of [...(justified.get(workerId) ?? [])].sort()) {
        notes.push(`Falta justificada el ${d}`);
      }
      for (const d of [...(lateJustified.get(workerId) ?? [])].sort()) {
        notes.push(`Tardanza justificada el ${d}`);
      }
      for (const n of a.feriados.slice().sort()) notes.push(n);
      /**
       * Nota de tarifas mezcladas. Va SIEMPRE que haya más de una, gane una o empaten: la celda
       * «Monto diario» muestra un solo número en el caso mayoritario, y sin esta nota ese número
       * haría creer que la persona cobró esa tarifa todos sus días.
       */
      const tarifas = desglose(a);
      if (tarifas.length > 1) {
        notes.push(
          'Tarifas mezcladas en el periodo: '
          + tarifas
            .map(([t, d]) => `S/${num(t)} en ${num(d)} día${d === 1 ? '' : 's'}`)
            .join(' y ')
          + (tarifaDominante(a) === null ? ' (empatan: la planilla muestra las dos)' : ''),
        );
      }
      if (a.unvalidated === 1) notes.push('1 asistencia sin validar');
      else if (a.unvalidated > 1) notes.push(`${a.unvalidated} asistencias sin validar`);

      /**
       * Bono por asistencia perfecta.
       *
       * La regla la fijó el usuario el 1-sep: **se paga si asistió a todos sus días asignados**, y
       * un turno corto NO lo rompe —*«puede ser por una emergencia … igual fue»*—. Por eso se mide
       * con `diasConIngreso` y no con `daysWorked`, que baja a 0.5 en un día corto.
       *
       * Se exige `asignados > 0`: sin días asignados no hay nada a lo que asistir, y regalar el
       * bono a quien no tenía turno convertiría el bono en un sueldo.
       */
      const asignados = diasAsignados.get(workerId) ?? 0;
      const asistidos = a.diasConIngreso.size;
      let bono = 0;
      if (montoBono > 0 && asignados > 0 && asistidos >= asignados) {
        bono = roundMoney(montoBono);
        notes.push(
          `Bono de asistencia S/${num(bono)}: asistió a sus ${asignados} `
          + `día${asignados === 1 ? '' : 's'} asignado${asignados === 1 ? '' : 's'}`,
        );
      } else if (montoBono > 0 && asignados > 0) {
        notes.push(
          `Sin bono de asistencia: asistió ${asistidos} de ${asignados} días asignados`,
        );
      }

      return {
        workerId,
        daysWorked: roundMoney(a.daysWorked),
        hoursWorked: a.hoursWorked,
        amount: roundMoney(a.amount + bono),
        bono,
        feriado: a.recargoFeriado,
        rateAmount: tarifaDominante(a),
        rateBreakdown: desglose(a),
        notes,
      };
    });
}
